/**
 * Copyright 2026 Salesforce, Inc.
 *
 * Licensed under the Apache License, Version 2.0 (the "License");
 * you may not use this file except in compliance with the License.
 * You may obtain a copy of the License at
 *
 *     http://www.apache.org/licenses/LICENSE-2.0
 *
 * Unless required by applicable law or agreed to in writing, software
 * distributed under the License is distributed on an "AS IS" BASIS,
 * WITHOUT WARRANTIES OR CONDITIONS OF ANY KIND, either express or implied.
 * See the License for the specific language governing permissions and
 * limitations under the License.
 */
import { useCallback, useMemo, type JSX } from 'react';
import { useLocation, useNavigation } from 'react-router';
import { useNavigate } from '@/hooks/use-navigate';

import type { ShopperSearch } from '@/scapi';
import { Button } from '@/components/ui/button';
import { cn } from '@/lib/utils';
import { useBrand } from '@/lib/brand/use-brand';
import type { Brand } from '@/lib/brand/brand';
import { X as Close } from 'lucide-react';
import { useTranslation } from 'react-i18next';
// @sfdc-extension-block-start SFDC_EXT_BOPIS
import { useStoreLocator } from '@/extensions/store-locator/providers/store-locator';
import { type TFunction } from 'i18next';
// @sfdc-extension-line SFDC_EXT_BOPIS
import { STORE_AVAILABILITY_REFINEMENT } from '@/extensions/bopis/constants';
// @sfdc-extension-block-end SFDC_EXT_BOPIS

// Get human-readable label for a specific value
const getValueLabel = (
    attributeId: string,
    value: string,
    refinements: ShopperSearch.schemas['ProductSearchRefinement'][],
    // @sfdc-extension-line SFDC_EXT_BOPIS
    selectedStoreInfo: { id?: string; inventoryId?: string; name?: string } | null,
    // @sfdc-extension-line SFDC_EXT_BOPIS
    tBopis: TFunction<'extBopis'>
): string => {
    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    if (attributeId === STORE_AVAILABILITY_REFINEMENT) {
        // The refine VALUE is the store id, and the chip needs to name the store. Fall
        // back to the generic wording only when the refine actually belongs to the
        // currently-selected store; otherwise show the raw value rather than assert
        // "in stock at your store" about a store this session has not selected (a
        // shared or bookmarked URL can carry someone else's store id).
        if (selectedStoreInfo?.name && selectedStoreInfo?.id === value) {
            return tBopis('storeInventoryFilter.label', { storeName: selectedStoreInfo.name });
        }
        return value;
    }
    // @sfdc-extension-block-end SFDC_EXT_BOPIS
    const refinement = refinements.find((r) => r.attributeId === attributeId);
    const valueObj = refinement?.values?.find((v) => v.value === value);
    return valueObj?.label || value;
};

/**
 * The clear-control's copy is a REAL per-brand difference, not a token.
 *
 * Live NM writes "Clear all"; live BG writes "Clear All". Read off the owner's
 * captures of 2026-08-23 in `.reference/live-shots/owner-2026-08-23-filters/` —
 * `filters-5-49-01PM.png` (NM, 1702 CSS) and `filters-5-49-14PM.png` (NM, 1728)
 * against `filters-5-53-11PM.png` (BG, 1728) — and
 * corroborated in BG's own saved markup, which emits
 * `<span class="clear-all">Clear All</span>` verbatim.
 *
 * A `Record<Brand, …>` rather than a `brand === 'nm'` branch, matching
 * `group-order.ts` in this same directory: an unhandled brand is a type error
 * instead of a silent fall-through to whichever arm was written first.
 */
const CLEAR_ALL_BY_BRAND: Record<Brand, { key: string; fallback: string }> = {
    nm: { key: 'categoryRefinements:clearAll', fallback: 'Clear all' },
    bg: { key: 'categoryRefinements:clearAllTitleCase', fallback: 'Clear All' },
};

/**
 * BELOW `md` THE TWO BRANDS PAINT DIFFERENT THINGS, so this is a band-level
 * difference rather than a token one.
 *
 * BG collapses the whole band to a SINGLE control. Owner capture
 * `.reference/live-shots/owner-2026-08-23-filters/filters-5-53-30PM.png` —
 * live BG at 400 CSS with four filters applied — shows
 * `133 Items` and then one light-grey pill reading `Clear Filters` with a
 * close glyph. No title, no bordered box, and NO PER-VALUE CHIPS AT ALL: the
 * four selected values are not listed anywhere on the results page, they are
 * reachable only inside the filter pane, where `filters-5-53-36PM.png` in that
 * same directory shows them as `Size (2)` and `Price (2)`.
 *
 * NM's arm is UNMEASURED — the owner's 2026-08-23 filter set covers BG at 400
 * and NM only at 1702/1728, so no live NM mobile capture exists in it. `box` is
 * therefore the CURRENT behaviour retained deliberately, not a claim about what
 * live NM does. If an NM mobile capture turns up and disagrees, this is the one
 * line that changes.
 */
const MOBILE_BAND_BY_BRAND: Record<Brand, 'clear-chip' | 'box'> = {
    nm: 'box',
    bg: 'clear-chip',
};

export default function CategoryFilters({
    result,
    placement = 'content',
}: {
    result: ShopperSearch.schemas['ProductSearchResult'];
    /**
     * `rail` is the live position on both brands: the box sits at the TOP of
     * the facet rail, above the first group. `content` is the pre-existing
     * mount in the results column, kept for widths below `md` where the rail
     * is not rendered at all.
     */
    placement?: 'rail' | 'content';
}): JSX.Element | null {
    const { t } = useTranslation();
    const brand = useBrand();
    const { key: clearAllKey, fallback: clearAllFallback } = CLEAR_ALL_BY_BRAND[brand];
    const mobileBand = MOBILE_BAND_BY_BRAND[brand];
    const navigate = useNavigate();
    const location = useLocation();
    const navigation = useNavigation();
    const isPending = navigation.state !== 'idle';
    const refinements = useMemo(() => result?.refinements || [], [result]);

    /**
     * Optimistic search string derived from the in-flight navigation target.
     *
     * While a navigation is pending, `navigation.location` holds the target location, allowing us to read the
     * intended refinements immediately. Once the navigation settles, we fall back to the current location.
     */
    const effectiveSearch = navigation.location ? navigation.location.search : location.search;

    // @sfdc-extension-block-start SFDC_EXT_BOPIS
    const selectedStoreInfo = useStoreLocator((s) => s.selectedStoreInfo);
    const { t: tBopis } = useTranslation('extBopis');
    // @sfdc-extension-block-end SFDC_EXT_BOPIS

    const activeFilters = useMemo(() => {
        const params = new URLSearchParams(effectiveSearch);
        const refines = params.getAll('refine');
        const filters: Array<{
            attributeId: string;
            value: string;
            groupLabel: string | undefined;
            valueLabel: string;
        }> = [];

        for (const refine of refines) {
            const separatorIndex = refine.indexOf('=');
            if (separatorIndex === -1) continue;

            const attributeId = refine.substring(0, separatorIndex);
            const value = refine.substring(separatorIndex + 1);
            // Category selection is represented by quick filters, so keep cgid in URL for behavior
            // and clear-all, but hide it from applied-filter chips.
            if (attributeId === 'cgid') continue;

            const valueLabel = getValueLabel(
                attributeId,
                value,
                refinements,
                // @sfdc-extension-line SFDC_EXT_BOPIS
                selectedStoreInfo,
                // @sfdc-extension-line SFDC_EXT_BOPIS
                tBopis
            );
            filters.push({
                attributeId,
                value,
                /* The GROUP's own label, as the response names it, so the chip
                   can read `Gender: Kids` the way live does. Undefined when the
                   refinement is not in the response — the store-availability
                   group is injected client-side and has no entry — and the chip
                   then falls back to the bare value rather than inventing a
                   prefix. */
                groupLabel: refinements.find((r) => r.attributeId === attributeId)?.label,
                valueLabel,
            });
        }
        return filters;
    }, [
        effectiveSearch,
        refinements,
        // @sfdc-extension-block-start SFDC_EXT_BOPIS
        selectedStoreInfo,
        tBopis,
        // @sfdc-extension-block-end SFDC_EXT_BOPIS
    ]);

    // Remove a specific filter
    const removeFilter = useCallback(
        (attributeId: string, value: string) => {
            const params = new URLSearchParams(location.search);
            const refines = params.getAll('refine');
            const refinePair = `${attributeId}=${value}`;

            // Remove this specific refinement
            params.delete('refine');
            refines.filter((r) => r !== refinePair).forEach((r) => params.append('refine', r));

            params.set('offset', '0');
            return navigate({
                ...location,
                search: `?${params.toString()}`,
            });
        },
        [location, navigate]
    );

    // Clear all filters
    const clearAllFilters = useCallback(() => {
        const params = new URLSearchParams(location.search);
        params.delete('refine');
        params.set('offset', '0');

        void navigate({
            ...location,
            search: `?${params.toString()}`,
        });
    }, [location, navigate]);

    // Don't render if no active filters
    if (activeFilters.length === 0) {
        return null;
    }

    /* BG below `md`: one `Clear Filters` pill, not the box. See
       MOBILE_BAND_BY_BRAND. Only the CONTENT placement takes this branch — the
       rail placement is `hidden` below `md` anyway, and above `md` this whole
       component is the rail's box on both brands. */
    if (placement === 'content' && mobileBand === 'clear-chip') {
        return (
            <div
                data-testid="active-filters"
                data-placement={placement}
                data-band="clear-chip"
                className={cn('mb-4 md:hidden', isPending && 'pointer-events-none opacity-50 transition-opacity')}>
                <Button
                    variant="ghost"
                    data-testid="selected-filters-clear-all"
                    className="selected-filter-chip type-label-medium h-auto cursor-pointer justify-start"
                    onClick={clearAllFilters}>
                    <span className="selected-filter-chip-label">
                        {t('categoryRefinements:clearFilters', { defaultValue: 'Clear Filters' })}
                    </span>
                    <Close className="size-3 shrink-0" />
                </Button>
            </div>
        );
    }

    return (
        <div
            data-testid="active-filters"
            data-placement={placement}
            className={cn(
                'selected-filters-box mb-4',
                // The RAIL is the live position on both brands — see the module
                // note. The content-column mount stays for widths below `md`,
                // where the rail is `hidden` and this is the only chip UI there
                // is; above `md` it would be a second copy of the box.
                placement === 'content' && 'md:hidden',
                isPending && 'pointer-events-none opacity-50 transition-opacity'
            )}>
            <div
                data-testid="selected-filters-header"
                className="flex items-center justify-between gap-4 selected-filters-header">
                <span className="selected-filters-title">
                    {t('categoryRefinements:selectedFilters', { defaultValue: 'Selected Filters' })}
                </span>
                <Button
                    variant="link"
                    data-testid="selected-filters-clear-all"
                    className="type-label-medium m-0 h-auto cursor-pointer p-0 underline"
                    onClick={clearAllFilters}>
                    {t(clearAllKey, { defaultValue: clearAllFallback })}
                </Button>
            </div>
            {/* One chip PER LINE, which is what both brands paint — and what
                live's own markup does: BG's saved page emits four sibling
                `<label>`s inside one `.container`, not a wrapping row. */}
            <div data-testid="selected-filters-list" className="selected-filters-list flex flex-col items-start">
                {activeFilters.map(({ attributeId, value, groupLabel, valueLabel }) => (
                    <Button
                        key={`${attributeId}:${value}`}
                        variant="ghost"
                        data-testid="selected-filter-chip"
                        className="selected-filter-chip type-label-medium h-auto cursor-pointer justify-start"
                        onClick={() => void removeFilter(attributeId, value)}>
                        {/* `Gender: Kids`, not `Kids`. Live prefixes every chip
                            with its group's label on both brands; BG's markup
                            splits it into a `Type:` span and a value span. */}
                        {groupLabel ? (
                            <span className="selected-filter-chip-label">
                                <span className="selected-filter-chip-group">{groupLabel}:</span> {valueLabel}
                            </span>
                        ) : (
                            <span className="selected-filter-chip-label">{valueLabel}</span>
                        )}
                        <Close className="size-3 shrink-0" />
                    </Button>
                ))}
            </div>
        </div>
    );
}
